import React from "react";
import { 
  FaCodeBranch, 
  FaDocker, 
  FaGitAlt, 
  FaServer, 
  FaShieldAlt, 
  FaRobot,
  FaCheck,
  FaArrowRight,
  FaSync,
  FaExclamationTriangle,
  FaCogs,
  FaCloud,
  FaCode,
  FaRocket
} from "react-icons/fa";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import devopsBg from "../../assets/DevOps-bg.webp";

const DevOpsServices = () => {
  const services = [
    {
      icon: FaCodeBranch,
      title: "CI/CD Pipeline",
      description: "End-to-end continuous integration and delivery pipeline setup",
      image: "⚡"
    },
    {
      icon: FaDocker,
      title: "Containerization",
      description: "Docker and Kubernetes implementation for microservices",
      image: "🐳"
    },
    {
      icon: FaGitAlt,
      title: "Version Control",
      description: "Git workflow optimization and branching strategies",
      image: "📚"
    },
    {
      icon: FaServer,
      title: "Infrastructure as Code",
      description: "Terraform, CloudFormation, and Ansible implementation",
      image: "🏗️"
    },
    {
      icon: FaShieldAlt,
      title: "DevSecOps",
      description: "Security integration throughout the development lifecycle",
      image: "🔒"
    },
    {
      icon: FaRobot,
      title: "Automation",
      description: "Complete automation of testing, deployment, and monitoring",
      image: "🤖"
    }
  ];

  const challenges = [
    {
      name: "CI/CD Complexity",
      description: "Managing complex deployment pipelines across multiple environments",
      severity: "High",
      icon: FaCodeBranch
    },
    {
      name: "Infrastructure Drift",
      description: "Configuration inconsistencies between environments over time",
      severity: "Medium",
      icon: FaServer
    },
    {
      name: "Security Integration",
      description: "Incorporating security checks without slowing down development",
      severity: "High",
      icon: FaShieldAlt
    },
    {
      name: "Tool Sprawl",
      description: "Managing and integrating numerous DevOps tools and platforms",
      severity: "Medium",
      icon: FaCogs
    },
    {
      name: "Monitoring Complexity",
      description: "Implementing comprehensive observability across microservices",
      severity: "High",
      icon: FaExclamationTriangle
    },
    {
      name: "Team Collaboration",
      description: "Breaking down silos between development and operations teams",
      severity: "Medium",
      icon: FaSync
    }
  ];

  const solutions = [
    {
      area: "CI/CD Automation",
      measures: ["Jenkins Pipelines", "GitLab CI/CD", "GitHub Actions", "ArgoCD", "Automated Testing", "Blue-Green Deployments"],
      icon: FaRocket
    },
    {
      area: "Container & Orchestration",
      measures: ["Docker Containers", "Kubernetes Clusters", "Helm Charts", "Service Mesh", "Auto-scaling", "Container Security"],
      icon: FaDocker
    },
    {
      area: "Infrastructure as Code",
      measures: ["Terraform Modules", "Ansible Playbooks", "CloudFormation", "Pulumi", "Configuration Management", "State Management"],
      icon: FaCode
    },
    {
      area: "Monitoring & Observability",
      measures: ["Prometheus & Grafana", "ELK Stack", "Distributed Tracing", "Log Aggregation", "Alerting Systems", "Performance Metrics"],
      icon: FaCloud
    }
  ];

  return (
    <div className="bg-gray-50">
      <Navbar />
      <div className="min-h-screen">
        {/* Hero Section - Exact match with Cybersecurity */}
        <div
          className="relative bg-cover bg-center bg-no-repeat text-white py-24 overflow-hidden"
          style={{ backgroundImage: `url(${devopsBg})` }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-blue-500 to-transparent opacity-20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-blue-400 to-transparent opacity-20 rounded-full translate-y-1/2 -translate-x-1/2"></div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent pb-3">
                DevOps Services
              </h1>
              <p className="text-xl text-blue-100 mb-10 leading-relaxed max-w-3xl mx-auto">
                Accelerate delivery, improve reliability, and enhance collaboration with our expert DevOps solutions.
              </p>
            </div>
          </div>
        </div>

        {/* Services Section - 3-column layout */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-50 to-transparent"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Our DevOps Services
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Comprehensive DevOps solutions to streamline your development lifecycle and accelerate delivery
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-blue-100 hover:border-blue-200 hover:transform hover:-translate-y-2"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-4xl bg-gradient-to-r from-blue-600 to-blue-800 p-4 rounded-2xl text-white">
                      {service.image}
                    </div>
                    <div className="text-blue-600">
                      <service.icon className="text-3xl opacity-80" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Challenges Section - 3-column layout */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-blue-50 to-gray-100 relative">
          <div className="absolute inset-0 bg-blue-900 opacity-5"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Common DevOps Challenges
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Addressing key hurdles in implementing effective DevOps practices
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {challenges.map((challenge, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-xl mr-4 ${
                      challenge.severity === 'Critical' ? 'bg-red-100 text-red-600' :
                      challenge.severity === 'High' ? 'bg-orange-100 text-orange-600' :
                      'bg-yellow-100 text-yellow-600'
                    }`}>
                      <challenge.icon className="text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{challenge.name}</h3>
                      <span className={`text-sm font-semibold ${
                        challenge.severity === 'Critical' ? 'text-red-600' :
                        challenge.severity === 'High' ? 'text-orange-600' :
                        'text-yellow-600'
                      }`}>
                        Severity: {challenge.severity}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {challenge.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Solutions Section - 2-column detailed layout */}
        <div className="pt-10 pb-20 bg-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-50 rounded-full translate-y-48 -translate-x-48"></div>
          
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Our DevOps Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive tools and methodologies to build, deploy, and manage applications efficiently
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {solutions.map((solution, index) => (
                <div key={index} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 h-full transform group-hover:scale-105 transition-all duration-500">
                    <div className="flex items-center mb-6">
                      <div className="bg-white bg-opacity-20 p-3 rounded-xl mr-4">
                        <solution.icon className="text-2xl" />
                      </div>
                      <h3 className="text-2xl font-bold">{solution.area}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {solution.measures.map((measure, idx) => (
                        <div key={idx} className="flex items-center text-blue-100">
                          <FaCheck className="text-green-300 mr-2 text-sm" />
                          <span className="text-sm">{measure}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative pt-10 pb-10 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-black opacity-10"></div>
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-blue-400 opacity-10 rounded-full -translate-y-1/4 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-blue-300 opacity-10 rounded-full translate-y-1/4 -translate-x-1/4"></div>
          
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-5xl font-bold text-white mb-8">
              Ready to Transform Your DevOps?
            </h2>
            <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Let us help you implement DevOps best practices and accelerate your software delivery. 
              From CI/CD pipelines to infrastructure automation, we'll guide you through every step of your DevOps journey.
            </p>
            <div className="flex justify-center items-center mt-6">
              <button 
                onClick={() => (window.location.href = "/contact-us")}
                className="group bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-12 py-5 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center gap-3"
              >
                Start DevOps Transformation
                <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-200" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DevOpsServices;